import PropTypes from "prop-types";

import { cn } from "@/lib/utils";
import RichTextEditor from "components/RichEditor/RichTextEditor";
import { isDraftRawState } from "components/RichEditor/legacy/draftToLexical";

const EDITED_THRESHOLD_MS = 1000;

// created_at and updated_at receive separate defaults on insert, so they
// differ by microseconds on every note; only a later update counts.
export function wasNoteEdited(note) {
  return (
    !!note.updated_at &&
    !!note.created_at &&
    new Date(note.updated_at) - new Date(note.created_at) > EDITED_THRESHOLD_MS
  );
}

/**
 * Resolves a stored note into `RichTextEditor` props.
 *
 * A Lexical document and a Draft.js raw state both go through `editorState`,
 * because the editor converts a Draft state at read time. A note whose state
 * cannot be parsed still has the HTML it exported at save time.
 */
export function noteToEditorProps(note) {
  const parsed = parseEditorState(note.editor_state);
  if (parsed) {
    return { editorState: parsed };
  }
  return note.rich_text ? { content: note.rich_text } : {};
}

function parseEditorState(stored) {
  if (!stored) return null;
  const parsed = typeof stored === "string" ? JSON.parse(stored) : stored;
  return parsed?.root || isDraftRawState(parsed) ? parsed : null;
}

// Read-only body of a note: a plain comment, a Lexical document, or a legacy
// Draft.js document.
function NoteContent({ note, className }) {
  if (note.info?.comment) {
    return <p className={cn("tw:mb-0!", className)}>{note.info.comment}</p>;
  }

  const editorProps = noteToEditorProps(note);
  if (!editorProps.editorState && !editorProps.content) {
    // A legacy note may hold only plain_text, with no editor_state, rich_text
    // or comment; show it rather than rendering nothing.
    if (note.plain_text) {
      return <p className={cn("tw:mb-0!", className)}>{note.plain_text}</p>;
    }
    return null;
  }

  // The editor builds its document on mount, so remount it when the note
  // changes to show the edited content.
  return (
    <RichTextEditor
      key={note.updated_at}
      {...editorProps}
      readOnly
      className={className}
    />
  );
}

NoteContent.propTypes = {
  note: PropTypes.shape({
    editor_state: PropTypes.string,
    rich_text: PropTypes.string,
    plain_text: PropTypes.string,
    updated_at: PropTypes.string,
    info: PropTypes.shape({ comment: PropTypes.string }),
  }).isRequired,
  className: PropTypes.string,
};

export default NoteContent;
